import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  page: number;
  total: number;
  pageSize: number;
  onChange: (p: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}

export default function Pagination({
  page, total, pageSize, onChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20],
}: Props) {
  const totalPages = Math.ceil(total / pageSize);

  const pages: (number | "...")[] = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "...") {
      pages.push("...");
    }
  }

  if (total === 0) return null;

  return (
    <div className="flex items-center justify-between pt-3 border-t border-zinc-100 gap-2 flex-wrap">
      {/* Left: info + page size */}
      <div className="flex items-center gap-3">
        <p className="text-xs text-zinc-400">
          {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} / {total} mục
        </p>
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-zinc-400">Hiển thị</span>
            <select
              value={pageSize}
              onChange={e => { onPageSizeChange(Number(e.target.value)); onChange(1); }}
              className="text-xs border border-zinc-200 rounded-lg px-2 py-1 bg-white text-zinc-600 focus:outline-none focus:ring-1 focus:ring-emerald-400 cursor-pointer"
            >
              {pageSizeOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <span className="text-xs text-zinc-400">/ trang</span>
          </div>
        )}
      </div>

      {/* Right: page buttons — only show if more than 1 page */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onChange(page - 1)} disabled={page === 1}
            className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 disabled:opacity-30 touch-manipulation"
          >
            <ChevronLeft size={15} />
          </button>
          {pages.map((p, i) =>
            p === "..." ? (
              <span key={i} className="px-1 text-zinc-300 text-sm">…</span>
            ) : (
              <button
                key={p}
                onClick={() => onChange(p as number)}
                className={`w-7 h-7 rounded-lg text-xs font-semibold touch-manipulation transition-colors
                  ${p === page ? "bg-emerald-600 text-white" : "hover:bg-zinc-100 text-zinc-600"}`}
              >
                {p}
              </button>
            )
          )}
          <button
            onClick={() => onChange(page + 1)} disabled={page === totalPages}
            className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 disabled:opacity-30 touch-manipulation"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
